'use client'

import { getTestResultByIdService } from '@/api/services/testService'
import { TestResultDetails } from '@/api/services/types/resultsTypes'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/common/Button'
import { getImageUrl } from '@/utils/getImageUrl'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useParams } from 'react-router-dom'

interface TestDetailsProps {
  testName?: string
  testDate?: string
  testDetailsData?: TestResultDetails
  onBack?: () => void
  onDownloadPDF?: () => void
}

export default function ResultsHistoryDetails({ onBack }: TestDetailsProps) {
  const { id } = useParams<{ id: string }>()

  const { data, isLoading } = useQuery({
    queryKey: ['test-result', id],
    queryFn: async () => {
      if (!id) return null
      const response = await getTestResultByIdService(id)
      if (!response.success) {
        console.error('Błąd API:', response.data)
        return null
      }
      return response.data
    }
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] p-6">
        <div className="mx-auto max-w-4xl space-y-6">
          <Card className="border-none bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-[#1d5067]">
                Ładowanie wyników...
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-8">
                <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#2db2b8]"></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] p-6">
        <div className="mx-auto max-w-4xl space-y-6">
          <Card className="border-none bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-[#1d5067]">
                Nie znaleziono wyników
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-gray-600">
                Nie udało się załadować wyników testu. Sprawdź, czy podany identyfikator
                jest poprawny.
              </p>
              <Button
                className="flex items-center gap-2 border-[#1d5067] text-[#1d5067] hover:bg-[#1d5067] hover:text-white"
                onClick={onBack}
                variant="outline">
                <ArrowLeft className="h-4 w-4" />
                Powrót
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const displayData = data

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const moduleIds = displayData.testData?.modules.map((a) => a.id)

  const moduleSkillsMap = moduleIds?.reduce(
    (acc, moduleId) => {
      var skillsForModule = displayData.moduleSkills
        .filter((skill) => skill.testModuleId === moduleId)
        .sort((a, b) => b.skillScore - a.skillScore)
      if (moduleIds[0] === moduleId) {
        const moduleSkill = displayData.moduleSkills.find(
          (a) => a.testModuleId === moduleId
        )
        if (moduleSkill && moduleSkill?.moduleSkillPercentageScore >= 60) {
          skillsForModule = skillsForModule.slice(0, 2)
        } else {
          skillsForModule = skillsForModule.slice(0, 1)
        }
      } else {
        skillsForModule = skillsForModule.slice(0, 1)
      }
      acc[moduleId] = skillsForModule

      return acc
    },
    {} as Record<string, typeof displayData.moduleSkills>
  )

  const firstModuleSkills = moduleSkillsMap?.[moduleIds?.[0] || ''] || []
  const secondModuleSkills = moduleSkillsMap?.[moduleIds?.[1] || ''] || []

  const matchingCombinations = displayData.testSkillsCombinations?.filter(
    (combination) => {
      const skill1Matches =
        firstModuleSkills.some(
          (skill) =>
            skill.skill.id === combination.combinationDescriptionResponse.skill1.id
        ) ||
        secondModuleSkills.some(
          (skill) =>
            skill.skill.id === combination.combinationDescriptionResponse.skill1.id
        )

      const skill2Matches =
        firstModuleSkills.some(
          (skill) =>
            skill.skill.id === combination.combinationDescriptionResponse.skill2.id
        ) ||
        secondModuleSkills.some(
          (skill) =>
            skill.skill.id === combination.combinationDescriptionResponse.skill2.id
        )
      return skill1Matches && skill2Matches
    }
  )

  return (
    <div className="min-h-screen bg-[#f8f9fa] p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <div className="mb-12 bg-gradient-to-r from-[#2bcaca]/10 to-[#2b9999]/10 p-8 text-center shadow-sm">
          <h1 className="mb-6 text-4xl font-bold tracking-tight text-[#2bcaca]">
            🧾 RAPORT – POZNAJ SIEBIE
          </h1>
          <div className="mx-auto max-w-2xl space-y-3">
            <div className="flex items-center justify-center gap-2 text-lg">
              <span className="font-semibold text-gray-700">Data:</span>
              <span className="text-[#2b9999]">{formatDate(displayData.createdAt)}</span>
            </div>
            {displayData.testData?.title && (
              <div className="flex items-center justify-center gap-2 text-lg">
                <span className="font-semibold text-gray-700">Rodzaj testu:</span>
                <span className="text-[#2b9999]">{displayData.testData.title}</span>
              </div>
            )}
          </div>
        </div>

        {/* Analiza modułów */}
        {displayData.testData?.analyzeModulesSeparately &&
        displayData.testData?.modules?.length > 0 ? (
          <>
            {displayData.testData?.modules?.map((module) => {
              const topSkills = moduleSkillsMap?.[module.id] || []
              return (
                <Card key={module.id} className="w-full rounded-none border-[#2b9999]/30">
                  <CardHeader>
                    <CardTitle className="text-xl">📊 {module.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {topSkills.map((skill) => (
                      <div key={skill.id}>
                        <h3 className="mb-2 text-lg font-semibold">
                          🔬 {skill.skill.name}
                        </h3>
                        <p className="leading-relaxed text-gray-700">
                          {skill.skill.description}
                        </p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )
            })}

            {/* Opis połączenia cech */}
            {matchingCombinations?.length > 0 && (
              <Card className="w-full border-[#2b9999]/30">
                <CardHeader>
                  <CardTitle className="text-xl">
                    🧠 JAKI/A JESTEŚ? (OPIS TWOJEGO PROFILU)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {matchingCombinations?.map((combination, index) => (
                    <div key={index} className="mb-4">
                      <p className="leading-relaxed whitespace-pre-line text-gray-700">
                        {combination.combinationDescriptionResponse.description}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <>
            {displayData.testData?.purpose &&
              displayData.testSkills &&
              displayData.testSkills.length > 0 && (
                <Card className="w-full rounded-none border-[#2b9999]/30">
                  <CardHeader>
                    <CardTitle className="text-xl">
                      🎯 {displayData.testData.purpose}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {displayData.testSkills?.length > 0 && (
                      <div>
                        <h3 className="mb-3 text-lg font-semibold">
                          ✅ Najbardziej pasujące cechy:
                        </h3>
                        <div className="space-y-4">
                          {displayData.testSkills
                            .sort((a, b) => b.skillScore - a.skillScore)
                            .slice(0, 2)
                            .map((skillResult) => (
                              <div
                                className="border-l-4 border-[#2bcaca] pl-4"
                                key={skillResult.id}>
                                <h4 className="mb-2 text-lg font-semibold">
                                  {skillResult.skill.name}
                                </h4>
                                <p className="leading-relaxed text-gray-700">
                                  {skillResult.skill.description}
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

            {/* Opis połączenia cech */}
            {displayData.testSkillsCombinations?.length > 0 && (
              <Card className="w-full rounded-none border-[#2b9999]/30">
                <CardHeader>
                  <CardTitle className="text-xl">
                    🧠 JAKA JESTEŚ? (OPIS TWOJEGO PROFILU)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {displayData.testSkillsCombinations.map((combination, index) => (
                    <div className="mb-4" key={index}>
                      <p className="leading-relaxed whitespace-pre-line text-gray-700">
                        {combination.combinationDescriptionResponse.description}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Zawody */}
        {displayData.professions?.length > 0 && (
          <Card className="w-full rounded-none border-[#2b9999]/30">
            <CardHeader>
              <CardTitle className="text-xl">
                💼 Zawody, które mogą Ci się spodobać:
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                {displayData.professions.slice(0, 4).map((professionResult) => (
                  <div
                    className="flex gap-4 border border-[#2b9999]/10 p-4 transition-shadow hover:shadow-md"
                    key={professionResult.id}>
                    {professionResult.profession.imageFileId && (
                      <div className="h-48 w-32 flex-shrink-0">
                        <img
                          alt={professionResult.profession.name}
                          className="h-full w-full object-cover"
                          src={getImageUrl(professionResult.profession.imageFileId)}
                        />
                      </div>
                    )}
                    <div className="min-w-0 flex-grow">
                      <h4 className="mb-2 text-lg font-semibold">
                        {professionResult.profession.name}
                      </h4>
                      <p className="text-sm leading-relaxed text-gray-700">
                        {professionResult.profession.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tekst końcowy */}
        <Card className="w-full rounded-none border-2 border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-xl text-orange-800">
              ✨ Kilka słów na zakończenie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="leading-relaxed font-medium text-orange-900">
              Twój wynik to wskazówka, która może pomóc Ci lepiej zrozumieć siebie i
              świadomie podjąć decyzję o wyborze szkoły. To, jaką drogą pójdziesz, zależy
              od Ciebie – masz wpływ na swoje decyzje. Możesz próbować, odkrywać nowe
              rzeczy i zmieniać kierunek, jeśli poczujesz, że to potrzebne. Nie musisz
              znać wszystkich odpowiedzi od razu. Najważniejsze, żeby to, co robisz, było
              bliskie Tobie i dawało Ci poczucie sensu. Twoja przyszłość to podróż – i
              możesz ją tworzyć krok po kroku, w swoim tempie. 🚀
            </p>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4">
          <Button
            className="flex items-center gap-2 border-[#1d5067] text-[#1d5067] hover:bg-[#1d5067] hover:text-white"
            onClick={onBack}
            variant="outline">
            <ArrowLeft className="h-4 w-4" />
            Powrót
          </Button>
        </div>
      </div>
    </div>
  )
}
